import { SidebarInset, SidebarProvider } from '@ophelos/ui/components/sidebar';
import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useAIChat } from '@/contexts/AIChatContext';
import { useHubPathname } from '@/lib/global/hub-path';
import { useEnforceDemoModeAccess } from '@/lib/presentation-mode/access';
import { useWhatsAppMissingCountries } from '../../apps/admin/hooks/useWhatsAppRates';
import { useHub } from '../../contexts/HubContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { type AppNavGroup, getAppNavigation, shouldClearDebtType } from '../../lib/app-navigation';
import { hasModuleAccess, hasModuleViewAccess } from '../../lib/module-permissions';
import { useIsOperator } from '../../lib/user-role';
import { AIChatPanel } from '../ai-chat/AIChatPanel';
import { AIChatTrigger } from '../ai-chat/AIChatTrigger';
import { useShellPresentation } from '../context/ShellPresentationContext';
import { AppSidebar } from './AppSidebar';
import { HubSidebar } from './HubSidebar';

/** Generic badge enrichment; finance badges arrive from its presentation adapter. */
function useNavGroupsWithBadges(groups: AppNavGroup[] | undefined, pathname: string): AppNavGroup[] | undefined {
  const { isSystemAdmin } = useUserPermissions();
  const isAdmin = pathname.startsWith('/app/admin');
  const { data: missingWhatsAppRates } = useWhatsAppMissingCountries(isAdmin && isSystemAdmin);
  if (!groups) return groups;
  return groups.map((group) => ({
    ...group,
    items: group.items.map((item) => {
      if (item.to === '/app/admin/system-costs' && missingWhatsAppRates && missingWhatsAppRates.length > 0) {
        return { ...item, badge: missingWhatsAppRates.length };
      }
      return item;
    }),
  }));
}

/** Map URL paths to their required Hub module (view or admin gate). */
function getRequiredModule(pathname: string): string | null {
  if (pathname.startsWith('/app/customers')) return 'customer-care';
  if (pathname.startsWith('/app/campaigns')) return 'campaigns';
  if (pathname.startsWith('/app/ingestion')) return 'ingestion';
  if (pathname.startsWith('/app/ai')) return 'ai-agents';
  if (pathname.startsWith('/app/finances')) return 'finances';
  if (pathname.startsWith('/app/library')) return 'library';
  if (pathname.startsWith('/app/import-export')) return 'config';
  if (pathname.startsWith('/app/analytics')) return 'analytics';
  if (pathname.startsWith('/app/admin')) return 'admin';
  if (pathname.startsWith('/settings')) return 'settings';
  return null;
}

export function HubLayout() {
  const pathname = useHubPathname();
  const { resolvedOpenSource } = useAIChat();
  const isOperator = useIsOperator();
  const { hasPermission, isSystemAdmin } = useUserPermissions();
  const { selectedOperator, selectedClient, selectedDebtType, setSelectedDebtType } = useHub();
  const presentation = useShellPresentation();

  // Debt type is cleared only by this shared hidden-prefix mechanism, never by URL book parsing.
  useEffect(() => {
    if (selectedDebtType && shouldClearDebtType(pathname)) setSelectedDebtType(null, { quiet: true });
  }, [pathname, selectedDebtType, setSelectedDebtType]);

  const requiredModule = getRequiredModule(pathname);
  const hasAppAccess = !requiredModule || hasModuleAccess(hasPermission, requiredModule);
  const hasComingSoonAccess = hasModuleViewAccess(hasPermission, 'coming-soon');
  const hasAIChatAccess = hasModuleAccess(hasPermission, 'ai-agents');
  const hasFinanceAccess = hasModuleAccess(hasPermission, 'finances');
  const isFinanceRoute = requiredModule === 'finances';
  const isSettingsRoute = pathname.startsWith('/settings');
  const showAITrigger = hasAIChatAccess || (hasFinanceAccess && isFinanceRoute);
  // Settings pages open a scope preview in the panel from their own eye button; it has no AI in
  // it, so it needs no AI access. Without that access the panel mounts only while it is showing
  // the preview: `isOpen` persists across reloads but the explicit source does not, and the
  // fallback would be the AI chat.
  const showAIChat = showAITrigger || (isSettingsRoute && resolvedOpenSource === 'settings');

  const appNavResult = hasAppAccess
    ? (presentation.navigation ??
      getAppNavigation(pathname, isOperator, isSystemAdmin, {
        operator: selectedOperator?.name,
        client: selectedClient?.display_name,
        debtType: selectedDebtType?.name,
      }))
    : null;
  const isComingSoonApp = appNavResult?.comingSoon === true;
  const canAccessApp = hasAppAccess && (!isComingSoonApp || hasComingSoonAccess);
  const appNav = canAccessApp ? appNavResult : null;
  const enrichedGroups = useNavGroupsWithBadges(appNav?.groups, pathname);

  usePageTitle();
  useEnforceDemoModeAccess();

  return (
    <SidebarProvider>
      {appNav && enrichedGroups ? <AppSidebar appName={appNav.appName} groups={enrichedGroups} /> : <HubSidebar />}
      <SidebarInset className="flex flex-col max-md:h-screen md:h-(--page-main-height) max-md:border-none max-md:rounded-none border bg-background shadow-none! overflow-hidden">
        <main className="flex-1 overflow-hidden h-(--page-main-height)">
          <Outlet />
        </main>
      </SidebarInset>
      {showAIChat && <AIChatPanel />}
      {showAITrigger && <AIChatTrigger />}
    </SidebarProvider>
  );
}
